import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { NamedEntity, MessageService, LoaderService } from '@shared';
import { RestaurantItemService } from '../../services';

const ALL_CATEGORIES = new NamedEntity('-1', 'All');
const ALL_ENTREES = new NamedEntity('-1', 'All');
const ALL_KID_MEALS = new NamedEntity('-1', 'All');

export interface DeliveryPriceChange {
  dataItem: any;
  newDeliveryPrice: number;
}

export interface BulkDeliveryApplyResult {
  updatedCount: number;
}

@Component({
  selector: 'app-delivery-management',
  templateUrl: './delivery-management.component.html',
  styleUrl: './delivery-management.component.scss',
})
export class DeliveryManagementComponent implements OnInit {
  @Input() productType!: NamedEntity;
  @Input() categories: NamedEntity[] = [];
  @Input() tierId?: string;
  @Input() gridData: any[] = [];

  defaultCategory = ALL_CATEGORIES;
  selectedCategory: NamedEntity = ALL_CATEGORIES;

  comboEntrees: NamedEntity[] = [];
  kidMealParIds: string[] = [];

  @Output() applyBulkChange = new EventEmitter<DeliveryPriceChange[]>();
  @Output() bulkApplied = new EventEmitter<BulkDeliveryApplyResult>();
  @Output() close = new EventEmitter<void>();

  showConfirm = false;
  changeType: 'percent' | 'dollar' | 'overall' = 'percent';
  percentageChange: number | null = 0.01;
  dollarChange: number | null = 1.00;
  overallPercentage: number | null = 0.21;
  roundDirection: 'Up' | 'Down' = 'Down';
  roundToNearest: '0' | '5' | '9' = '5';

  affectedCount: number | null = null;

  get isCombo(): boolean {
    return this.productType?.id === 'combo' || this.productType?.id === 'kidmeal';
  }

  get isKidMeal(): boolean {
    return this.productType?.id === 'kidmeal';
  }

  get defaultEntree(): NamedEntity {
    return this.isKidMeal ? ALL_KID_MEALS : ALL_ENTREES;
  }

  selectedEntree: NamedEntity = ALL_ENTREES;

  constructor(
    private restaurantItemService: RestaurantItemService,
    private messageService: MessageService,
    private loaderService: LoaderService,
  ) {}

  ngOnInit() {
    if (this.isKidMeal) {
      const seen = new Set<string>();
      this.gridData.forEach(item => {
        if (item.comboParId && !seen.has(item.comboParId)) {
          seen.add(item.comboParId);
          this.kidMealParIds.push(item.comboParId);
          this.comboEntrees.push(new NamedEntity(item.comboParId, item.comboName));
        }
      });
      this.comboEntrees.sort((a, b) => a.name.localeCompare(b.name));
      this.selectedEntree = ALL_KID_MEALS;
    } else if (this.productType?.id === 'combo') {
      this.restaurantItemService.getComboEntrees(parseInt(this.tierId ?? '0')).subscribe({
        next: (entrees) => {
          this.comboEntrees = entrees.map(e => new NamedEntity(e.parItemId, e.itemName));
        },
      });
      this.selectedEntree = ALL_ENTREES;
    }
    this.fetchCount();
  }

  fetchCount() {
    const comboParId = this.isCombo && this.selectedEntree.id !== '-1'
      ? parseInt(this.selectedEntree.id)
      : null;

    if (this.isKidMeal && comboParId === null) {
      if (this.kidMealParIds.length === 0) { this.affectedCount = 0; return; }
      forkJoin(
        this.kidMealParIds.map(parId =>
          this.restaurantItemService.getBulkDeliveryPctChangeCount(
            parseInt(this.tierId ?? '0'), 'combo', null, parseInt(parId),
          )
        )
      ).subscribe({
        next: (results) => this.affectedCount = results.reduce((sum, r) => sum + r.count, 0),
        error: () => (this.affectedCount = null),
      });
      return;
    }

    const categoryId = !this.isCombo && this.selectedCategory.id !== '-1'
      ? parseInt(this.selectedCategory.id)
      : null;

    const productTypeId = this.isKidMeal ? 'combo' : this.productType.id;

    this.restaurantItemService
      .getBulkDeliveryPctChangeCount(
        parseInt(this.tierId ?? '0'),
        productTypeId,
        categoryId,
        comboParId,
      )
      .subscribe({
        next: (result) => (this.affectedCount = result.count),
        error: () => (this.affectedCount = null),
      });
  }

  get changeValue(): number | null {
    if (this.changeType === 'percent') return this.percentageChange;
    if (this.changeType === 'dollar') return this.dollarChange;
    return this.overallPercentage;
  }

  confirmApply() {
    if (this.changeValue == null) return;
    this.showConfirm = true;
  }

  apply() {
    this.showConfirm = false;
    if (this.changeValue == null) return;

    this.loaderService.show();

    const comboParId = this.isCombo && this.selectedEntree.id !== '-1'
      ? parseInt(this.selectedEntree.id)
      : null;

    if (this.isKidMeal && comboParId === null) {
      forkJoin(
        this.kidMealParIds.map(parId =>
          this.restaurantItemService.applyBulkDeliveryPctChange({
            tierId: parseInt(this.tierId ?? '0'),
            productTypeId: 'combo',
            categoryId: null,
            comboParId: parseInt(parId),
            amountChange: this.changeValue!,
            changeType: this.changeType,
            roundDirection: this.roundDirection,
            roundToNearest: parseInt(this.roundToNearest),
          })
        )
      ).subscribe({
        next: (results) => {
          const totalUpdated = results.reduce((sum, r) => sum + r.updatedCount, 0);
          this.loaderService.hide();
          this.messageService.success(`${totalUpdated} delivery prices updated. Click Schedule Changes to sync.`);
          this.bulkApplied.emit({ updatedCount: totalUpdated });
          this.close.emit();
        },
        error: (err) => {
          this.loaderService.hide();
          this.messageService.error(err);
        },
      });
      return;
    }

    const categoryId = !this.isCombo && this.selectedCategory.id !== '-1'
      ? parseInt(this.selectedCategory.id)
      : null;

    const productTypeId = this.isKidMeal ? 'combo' : this.productType.id;

    this.restaurantItemService
      .applyBulkDeliveryPctChange({
        tierId: parseInt(this.tierId ?? '0'),
        productTypeId,
        categoryId,
        comboParId,
        amountChange: this.changeValue!,
        changeType: this.changeType,
        roundDirection: this.roundDirection,
        roundToNearest: parseInt(this.roundToNearest),
      })
      .subscribe({
        next: (result) => {
          this.loaderService.hide();
          this.messageService.success(`${result.updatedCount} delivery prices updated. Click Schedule Changes to sync.`);
          this.bulkApplied.emit(result);
          this.close.emit();
        },
        error: (err) => {
          this.loaderService.hide();
          this.messageService.error(err);
        },
      });
  }
}
